import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AIHelpButton } from "@/components/VoiceInput";
import { toast } from "sonner";
import { Plus, Activity, Trash2, Heart, Droplet, Scale, Thermometer, Wind } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Vitals() {
  const { activeId, active } = useMembers();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today() });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/vitals?member_id=${activeId}`).then(r => setItems(r.data));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!activeId) { toast.error("Select a member first"); return; }
    setLoading(true);
    try {
      const payload = { ...form, member_id: activeId };
      ["bp_systolic","bp_diastolic","heart_rate","oxygen"].forEach(k => { if (payload[k]) payload[k] = parseInt(payload[k]); });
      ["sugar","weight","temperature"].forEach(k => { if (payload[k]) payload[k] = parseFloat(payload[k]); });
      await api.post("/vitals", payload);
      toast.success("Vitals saved");
      setOpen(false); setForm({ date: today() }); load();
    } catch { toast.error("Could not save"); } finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/vitals/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <Header title="Daily Vitals" subtitle={`Logging for ${active?.name || "—"}. Track BP, sugar, weight and more.`}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-vitals-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340] text-white">
                <Plus className="w-4 h-4 mr-2" /> Log Vitals
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Log Vitals</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="vitals-form">
                <Field label="Date"><Input type="date" data-testid="vitals-date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="BP Systolic"><Input data-testid="vitals-bp-sys" placeholder="120" value={form.bp_systolic || ""} onChange={e => setForm({...form, bp_systolic: e.target.value})} /></Field>
                  <Field label="BP Diastolic"><Input data-testid="vitals-bp-dia" placeholder="80" value={form.bp_diastolic || ""} onChange={e => setForm({...form, bp_diastolic: e.target.value})} /></Field>
                  <Field label="Blood Sugar"><Input data-testid="vitals-sugar" placeholder="mg/dL" value={form.sugar || ""} onChange={e => setForm({...form, sugar: e.target.value})} /></Field>
                  <Field label="Heart Rate"><Input data-testid="vitals-hr" placeholder="bpm" value={form.heart_rate || ""} onChange={e => setForm({...form, heart_rate: e.target.value})} /></Field>
                  <Field label="Weight"><Input data-testid="vitals-weight" placeholder="kg" value={form.weight || ""} onChange={e => setForm({...form, weight: e.target.value})} /></Field>
                  <Field label="Oxygen"><Input data-testid="vitals-spo2" placeholder="%" value={form.oxygen || ""} onChange={e => setForm({...form, oxygen: e.target.value})} /></Field>
                  <Field label="Temperature"><Input data-testid="vitals-temp" placeholder="°C" value={form.temperature || ""} onChange={e => setForm({...form, temperature: e.target.value})} /></Field>
                </div>
                <Field label="Notes"><Textarea data-testid="vitals-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} /></Field>
                <Button type="submit" disabled={loading} data-testid="save-vitals-btn" className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">{loading ? "Saving..." : "Save"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Activity} title="No vitals logged yet" subtitle="Start tracking your vitals daily for richer insights." />
      ) : (
        <div className="space-y-3">
          {items.map(v => (
            <Card key={v.id} className="p-5 rounded-2xl border border-[#E5E7E4] flex items-start justify-between" data-testid={`vital-row-${v.id}`}>
              <div>
                <p className="text-xs font-semibold tracking-[0.1em] uppercase text-[#7D8F84]">{v.date}</p>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                  {v.bp_systolic && <Metric icon={Heart} label="BP" value={`${v.bp_systolic}/${v.bp_diastolic}`} />}
                  {v.sugar && <Metric icon={Droplet} label="Sugar" value={`${v.sugar} mg/dL`} />}
                  {v.heart_rate && <Metric icon={Activity} label="HR" value={`${v.heart_rate} bpm`} />}
                  {v.weight && <Metric icon={Scale} label="Weight" value={`${v.weight} kg`} />}
                  {v.oxygen && <Metric icon={Wind} label="SpO₂" value={`${v.oxygen}%`} />}
                  {v.temperature && <Metric icon={Thermometer} label="Temp" value={`${v.temperature}°C`} />}
                </div>
                {v.notes && <p className="mt-3 text-sm text-[#5C6D63] italic">{v.notes}</p>}
              </div>
              <button data-testid={`del-vital-${v.id}`} onClick={() => del(v.id)} className="text-[#7D8F84] hover:text-[#C76B58] p-1.5 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function Field({ label, children, helpContext }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{label}</Label>
        {helpContext !== false && <AIHelpButton field={label} context={helpContext} />}
      </div>
      <div>{children}</div>
    </div>
  );
}
export function Metric({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-[#7D8F84]" /><span className="text-[#7D8F84]">{label}:</span><span className="font-medium text-[#1C2E24]">{value}</span></div>;
}
export function Header({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
      <div>
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7D8F84]">Records</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{title}</h1>
        <p className="text-[#5C6D63] mt-1 leading-relaxed">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}
export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <Card className="p-12 rounded-2xl border border-dashed border-[#E5E7E4] bg-white text-center">
      <Icon className="w-10 h-10 text-[#7D8F84] mx-auto mb-3" strokeWidth={1.5} />
      <h3 className="font-heading text-xl font-medium">{title}</h3>
      <p className="text-[#5C6D63] mt-1">{subtitle}</p>
    </Card>
  );
}
